import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  WatchOutlined,
  Battery80 as BatteryIcon,
  FavoriteBorder as HeartIcon,
  ThermostatAuto as TemperatureIcon,
  Sync as SyncIcon,
  SignalCellularAlt as SignalIcon,
} from '@mui/icons-material';
import api from '../services/api';
import PoolWearableBaselineDefaultsCard from './pool/PoolWearableBaselineDefaultsCard';

interface WearableRow {
  id: number;
  pool_id: number;
  name: string;
  code: string;
  type: string;
  status: string;
  is_active: boolean;
  battery_level: number | null;
  signal_strength: number | null;
  last_sync: string | null;
}

interface WearableVitals {
  heart_rate: number | null;
  temperature: number | null;
  signal_strength: number | null;
  battery_level: number | null;
  timestamp: string | null;
}

interface WearableStatusProps {
  poolId: number;
}

const typeLabel = (type: string) => {
  const t = String(type || '').toLowerCase();
  if (t === 'wristband' || t === 'watch' || t === 'band') return '손목밴드';
  if (t === 'anklet') return '발목밴드';
  if (t === 'necklace') return '목걸이';
  return type || '웨어러블';
};

const isActiveStatus = (status: string, isActive: boolean) => {
  const s = String(status || '').toLowerCase();
  return isActive !== false && (s === 'active' || s === '');
};

const getHeartRateColor = (heartRate: number) => {
  if (heartRate > 100) return 'error';
  if (heartRate > 90) return 'warning';
  return 'success';
};

const getTemperatureColor = (temp: number) => {
  if (temp > 37.5) return 'error';
  if (temp > 37.0) return 'warning';
  return 'success';
};

const getBatteryColor = (level: number) => {
  if (level < 20) return 'error';
  if (level < 50) return 'warning';
  return 'success';
};

const formatLastSync = (lastSyncTime: string | null | undefined) => {
  if (!lastSyncTime) return '기록 없음';
  const lastSync = new Date(lastSyncTime);
  if (Number.isNaN(lastSync.getTime())) return '기록 없음';
  const diffSecs = Math.floor((Date.now() - lastSync.getTime()) / 1000);
  if (diffSecs < 0) return lastSync.toLocaleString();
  if (diffSecs < 60) return `${diffSecs}초 전`;
  const diffMins = Math.floor(diffSecs / 60);
  if (diffMins < 60) return `${diffMins}분 전`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}시간 전`;
  return `${Math.floor(diffHours / 24)}일 전`;
};

const mapDevice = (raw: any): WearableRow => {
  const battery =
    typeof raw.battery_level === 'number'
      ? raw.battery_level
      : typeof raw.last_battery_level === 'number'
        ? raw.last_battery_level
        : null;
  const signal =
    typeof raw.last_signal_strength === 'number' ? raw.last_signal_strength : null;
  return {
    id: Number(raw.id),
    pool_id: Number(raw.pool_id),
    name: raw.name || raw.code || raw.device_id || `장치 #${raw.id}`,
    code: raw.code || raw.device_id || '',
    type: raw.device_type || raw.type || 'wristband',
    status: String(raw.status || 'inactive').toLowerCase(),
    is_active: raw.is_active !== false,
    battery_level: battery,
    signal_strength: signal,
    last_sync: raw.last_sync_at || raw.updated_at || raw.created_at || null,
  };
};

const mapVitals = (raw: any, fallbackSignal: number | null, fallbackBattery: number | null): WearableVitals => ({
  heart_rate: typeof raw?.heart_rate === 'number' ? raw.heart_rate : null,
  temperature:
    typeof raw?.body_temperature === 'number'
      ? raw.body_temperature
      : typeof raw?.temperature === 'number'
        ? raw.temperature
        : null,
  signal_strength:
    typeof raw?.signal_strength === 'number'
      ? raw.signal_strength
      : fallbackSignal,
  battery_level:
    typeof raw?.battery_level === 'number' ? raw.battery_level : fallbackBattery,
  timestamp: raw?.timestamp || null,
});

export const WearableStatus: React.FC<WearableStatusProps> = ({ poolId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [wearables, setWearables] = useState<WearableRow[]>([]);
  const [vitals, setVitals] = useState<Record<number, WearableVitals>>({});

  const load = useCallback(async (silent = false) => {
    if (!poolId) return;
    if (!silent) {
      setIsLoading(true);
      setError(null);
    }
    try {
      const listRes = await api.get(`/api/pools/${poolId}/wearables`);
      const rows = Array.isArray(listRes) ? listRes : listRes?.data;
      const devices: WearableRow[] = Array.isArray(rows) ? rows.map(mapDevice) : [];
      setWearables(devices);

      const nextVitals: Record<number, WearableVitals> = {};
      await Promise.all(
        devices.map(async (device) => {
          try {
            const sensor = await api.get(
              `/api/pools/${poolId}/wearables/${device.id}/sensor-data/latest`,
              undefined,
              { useCache: false, silentError: true },
            );
            const payload = sensor?.data ?? sensor;
            nextVitals[device.id] = mapVitals(
              payload,
              device.signal_strength,
              device.battery_level,
            );
            if (payload?.timestamp) {
              device.last_sync = payload.timestamp;
            }
          } catch {
            nextVitals[device.id] = {
              heart_rate: null,
              temperature: null,
              signal_strength: device.signal_strength,
              battery_level: device.battery_level,
              timestamp: device.last_sync,
            };
          }
        }),
      );
      setWearables([...devices]);
      setVitals(nextVitals);
      setError(null);
    } catch (e: any) {
      const detail =
        e?.response?.data?.detail || e?.message || '웨어러블 목록을 불러오기 실패';
      setError(String(detail));
      if (!silent) {
        setWearables([]);
        setVitals({});
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [poolId]);

  useEffect(() => {
    load(false);
    const timer = window.setInterval(() => load(true), 15000);
    return () => window.clearInterval(timer);
  }, [load]);

  const hrValues = Object.values(vitals)
    .map((v) => v.heart_rate)
    .filter((v): v is number => typeof v === 'number');
  const tempValues = Object.values(vitals)
    .map((v) => v.temperature)
    .filter((v): v is number => typeof v === 'number');

  if (isLoading) {
    return (
      <Paper sx={{ p: 2, height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error && wearables.length === 0) {
    return (
      <Paper sx={{ p: 2, height: '100%' }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (wearables.length === 0) {
    return (
      <Paper sx={{ p: 2, height: '100%' }}>
        <Alert severity="info">
          이 시설에 등록된 웨어러블 장치 없음. 시설 관리 → 웨어러블 관리에서 장치를 추가.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">웨어러블 장치 상태</Typography>
        <Tooltip title="새로고침">
          <IconButton onClick={() => load(false)} size="small">
            <SyncIcon />
          </IconButton>
        </Tooltip>
      </Box>
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>장치</TableCell>
              <TableCell align="center">상태</TableCell>
              <TableCell align="center">심박수</TableCell>
              <TableCell align="center">체온</TableCell>
              <TableCell align="center">배터리</TableCell>
              <TableCell align="center">신호</TableCell>
              <TableCell align="center">마지막 동기화</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {wearables.map((wearable) => {
              const data = vitals[wearable.id];
              const active = isActiveStatus(wearable.status, wearable.is_active);
              const battery =
                typeof data?.battery_level === 'number'
                  ? data.battery_level
                  : wearable.battery_level;
              const signal =
                typeof data?.signal_strength === 'number'
                  ? data.signal_strength
                  : wearable.signal_strength;
              const lastSync = data?.timestamp || wearable.last_sync;

              return (
                <TableRow key={wearable.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        sx={{
                          width: 30,
                          height: 30,
                          mr: 1,
                          bgcolor: active ? 'primary.main' : 'grey.400',
                        }}
                      >
                        <WatchOutlined fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2">{wearable.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {typeLabel(wearable.type)}
                          {wearable.code ? ` · ${wearable.code}` : ''}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={active ? '활성' : wearable.status || '비활성'}
                      color={active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {typeof data?.heart_rate === 'number' ? (
                      <Chip
                        size="small"
                        icon={<HeartIcon />}
                        label={`${Math.round(data.heart_rate)} bpm`}
                        color={getHeartRateColor(data.heart_rate)}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {typeof data?.temperature === 'number' ? (
                      <Chip
                        size="small"
                        icon={<TemperatureIcon />}
                        label={`${data.temperature.toFixed(1)}℃`}
                        color={getTemperatureColor(data.temperature)}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {typeof battery === 'number' ? (
                      <Chip
                        size="small"
                        icon={<BatteryIcon />}
                        label={`${Math.round(battery)}%`}
                        color={getBatteryColor(battery)}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {typeof signal === 'number' ? (
                      <Tooltip title={`신호: ${signal}`}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <SignalIcon
                            color={signal > 50 ? 'success' : signal > 20 ? 'warning' : 'error'}
                          />
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="caption">{formatLastSync(lastSync)}</Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          주요 활동 통계
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  심박수 평균
                </Typography>
                <Typography variant="h5" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                  <HeartIcon sx={{ mr: 1 }} color="error" />
                  {hrValues.length
                    ? `${Math.round(hrValues.reduce((a, b) => a + b, 0) / hrValues.length)} bpm`
                    : '-'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  체온 평균
                </Typography>
                <Typography variant="h5" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                  <TemperatureIcon sx={{ mr: 1 }} color="warning" />
                  {tempValues.length
                    ? `${(tempValues.reduce((a, b) => a + b, 0) / tempValues.length).toFixed(1)}℃`
                    : '-'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <PoolWearableBaselineDefaultsCard poolId={poolId} compact showSnackbar={undefined} />
    </Paper>
  );
};

export default WearableStatus;
